import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProducts,
  createStockItem,
  getStockList,
  getLastStockItemForEachVariety,
  getImportProfiles,
  getImportConversions,
  getCategories,
  getProviders,
  createProvider,
  uploadFile,
  addVarietyToProduct,
} from "../api"; // Replace with actual API functions
import ProviderFormFields from "../components/providers/ProviderFormFields";
import StockProductPickerPanel from "../components/StockProductPickerPanel";
import { formatRut } from "../utils/rut";
import InlineLoading from "../components/InlineLoading";
import {
  EMPTY_PROVIDER,
  createEmptyAccount,
  getProviderApiErrorMessage,
  validateProviderForm,
} from "../utils/provider";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";
import { resolvePriceClientType } from "../utils/clientType";
import { notify } from '../utils/alertBus';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';
import { calculateStockAmounts } from "../utils/stockCost";

// TODO: Hacer review de que lo que se compró efectivamente llegó (checklist e impresión)
const STATIC_CATEGORIES = [
  { id: "chama-fruit", label: "Frutas y Verduras" },
  { id: "chama-max", label: "Construcción" },
  { id: "chama-todo", label: "Otros Alimentos" },
];

const normalizeMetricUnit = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const toMetricUnitKey = (value) => {
  const normalized = normalizeMetricUnit(value);
  if (!normalized) return "";
  if (normalized.includes("kg") || normalized.includes("kilo")) return "kg";
  if (normalized.includes("caja")) return "caja";
  if (normalized.includes("cajon")) return "cajon";
  if (normalized.includes("saco")) return "saco";
  if (normalized.includes("malla")) return "malla";
  if (normalized.includes("unidad")) return "unidad";
  if (normalized.includes("balde")) return "balde";
  if (normalized.includes("gamela")) return "gamela";
  if (normalized.includes("plancha")) return "plancha";
  if (normalized.includes("bandeja")) return "bandeja";
  if (normalized.includes("bin")) return "bin";
  if (normalized.includes("granel")) return "granel";
  return normalized;
};

const StockCreatePage = () => {
  const { id } = useParams();
  const [modalVariety, setModalVariety] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState("");
  const [lastStockItems, setLastStockItems] = useState({});
  const [stockListVarietyCounts, setStockListVarietyCounts] = useState({});
  const [stockList, setStockList] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [stockPrice, setStockPrice] = useState("");
  const [stockPriceWithoutIva, setStockPriceWithoutIva] = useState("");
  const [metricUnit, setMetricUnit] = useState("");
  const [weight, setWeight] = useState("");
  const [lengthCm, setLengthCm] = useState("");
  const [widthCm, setWidthCm] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [importConversions, setImportConversions] = useState([]);
  const [conversionInputs, setConversionInputs] = useState({});
  const [loadingImportDefaults, setLoadingImportDefaults] = useState(false);
  const [bultosPerBin, setBultosPerBin] = useState("");
  const [providers, setProviders] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [providerSearchTerm, setProviderSearchTerm] = useState("");
  const [isProviderSelectOpen, setIsProviderSelectOpen] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [providerForm, setProviderForm] = useState(EMPTY_PROVIDER);
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [providerError, setProviderError] = useState("");
  const [isSavingProvider, setIsSavingProvider] = useState(false);
  const [isSavingStock, setIsSavingStock] = useState(false);
  const [payments, setPayments] = useState([]);
  const [modalProduct, setModalProduct] = useState(null);
  const [showNewVarietyModal, setShowNewVarietyModal] = useState(false);
  const [newVarietyName, setNewVarietyName] = useState("");
  const [newVarietyImage, setNewVarietyImage] = useState(null);
  const [newVarietyFile, setNewVarietyFile] = useState(null);
  const [newVarietyDescription, setNewVarietyDescription] = useState("");
  const branchId =
    useParams().branchId || localStorage.getItem("selectedBranchId");
  const { user } = useCurrentUser();
  const canReadProducts = hasPermission(user, "product.read");
  const canUpdateProduct = hasPermission(user, "product.update");
  const canReadStock = hasPermission(user, "stock.read");
  const canCreateStock = hasPermission(user, "stock.create");
  const canReadProvider = hasPermission(user, "provider.read");
  const canCreateProvider = hasPermission(user, "provider.create");
  const isImportList = (stockList?.listType || "standard") === "importaciones";

  const displayCategories = categories.length ? categories : STATIC_CATEGORIES;

  const resolveProductCategoryId = (product) => {
    return product?.categoryId ?? product?.Category?.id ?? null;
  };

  const resolveCategoryLabel = (category) =>
    category?.name || category?.label || String(category?.id ?? "");

  const providerSelectRef = useRef(null);

  const navigate = useNavigate();
  const isBinUnit = (value) => String(value || "").trim().toLowerCase() === "bin";
  const paymentMethods = [
    { id: "efectivo", label: "Efectivo" },
    { id: "transferencia", label: "Transferencia" },
    { id: "tarjeta", label: "Tarjeta" },
    { id: "cheque", label: "Cheque" },
  ];
  const today = new Date().toISOString().split("T")[0];
  const modalInputClass =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:bg-slate-100 disabled:text-slate-500";
  const modalLabelClass = "block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1";
  const modalSectionClass = "rounded-lg border border-slate-200 bg-white p-4 shadow-sm";

  const fetchProviders = useCallback(async () => {
    try {
      if (!canReadProvider) {
        setAllProviders([]);
        setProviders([]);
        return;
      }
      const data = await getProviders({ includeDeleted: true, pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      const providerRows = normalized.data || [];
      setAllProviders(providerRows);
      setProviders(providerRows.filter((provider) => !provider.isDeleted));
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  }, [canReadProvider]);

  const fetchPrices = useCallback(async () => {
    try {
      if (!canReadProducts) {
        setProducts([]);
        setFilteredProducts([]);
        return;
      }
      const data = await getProducts(branchId, { pageSize: 0 });
      const normalizedProducts = normalizePaginatedResponse(data);
      setProducts(normalizedProducts.data || []);
      if (canReadStock) {
        const stockData = await getLastStockItemForEachVariety(branchId, { pageSize: 0 });
        const normalizedStock = normalizePaginatedResponse(stockData);
        const stockItemsMap = (normalizedStock.data || []).reduce((acc, stockItem) => {
          acc[stockItem.varietyId] = stockItem;
          return acc;
        }, {});
        setLastStockItems(stockItemsMap);
      } else {
        setLastStockItems({});
      }
    } catch (error) {
      console.error("Error fetching prices:", error);
    }
  }, [branchId, canReadProducts, canReadStock]);

  const fetchStockListVarietyStatus = useCallback(async () => {
    if (!branchId || !id || !canReadStock) {
      setStockListVarietyCounts({});
      return;
    }
    try {
      const stockListData = await getStockList(branchId, id);
      setStockList(stockListData || null);
      const counts = (stockListData?.Stocks || []).reduce((acc, stockItem) => {
        const varietyId = Number(stockItem?.varietyId);
        if (!Number.isInteger(varietyId)) return acc;
        acc[varietyId] = (acc[varietyId] || 0) + 1;
        return acc;
      }, {});
      setStockListVarietyCounts(counts);
    } catch (error) {
      console.error("Error fetching stock list variety status:", error);
    }
  }, [branchId, id, canReadStock]);

  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      fetchPrices();
      return;
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products, fetchPrices]);

  useProductEvents(branchId, handleProductEvent);

  useEffect(() => {
    fetchPrices();
    fetchProviders();
    fetchStockListVarietyStatus();
  }, [fetchPrices, fetchProviders, fetchStockListVarietyStatus]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!branchId) return;
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const data = await getCategories({ branchId });
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
        setCategoriesError("No se pudieron cargar las categorías.");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [branchId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        providerSelectRef.current &&
        !providerSelectRef.current.contains(event.target)
      ) {
        setIsProviderSelectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleProviderFormChange = (e) => {
    const { name, value } = e.target;
    setProviderForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProviderRutChange = (e) => {
    setProviderForm((prev) => ({ ...prev, rut: formatRut(e.target.value) }));
  };

  const handleProviderAccountChange = (index, field, value) => {
    setProviderAccounts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddProviderAccount = () => {
    if (!canCreateProvider) return;
    setProviderAccounts((prev) => [...prev, createEmptyAccount()]);
  };

  const handleRemoveProviderAccount = (account, index) => {
    if (!canCreateProvider) return;
    if (!account.id) {
      setProviderAccounts((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    setProviderAccounts((prev) => prev.filter((_, i) => i !== index));
  };

  const resetProviderForm = () => {
    setProviderForm(EMPTY_PROVIDER);
    setProviderAccounts([]);
    setProviderError("");
  };

  const handleProviderModalClose = () => {
    setShowProviderModal(false);
    resetProviderForm();
  };

  const handleSaveProvider = async (e) => {
    e.preventDefault();
    if (!canCreateProvider) return;
    setProviderError("");
    const {
      payload: providerPayload,
      accountPayloads,
      error: validationError,
    } = validateProviderForm({
      form: providerForm,
      accounts: providerAccounts,
      providers: allProviders,
    });
    if (validationError) {
      setProviderError(validationError);
      return;
    }
    setIsSavingProvider(true);
    try {
      const payload = {
        ...providerPayload,
        bankAccounts: accountPayloads.map(({ id: _, ...accountPayload }) => accountPayload),
      };
      const created = await createProvider(payload);
      const nextAllProviders = [...allProviders, created].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
      const nextProviders = nextAllProviders.filter((provider) => !provider.isDeleted);
      setAllProviders(nextAllProviders);
      setProviders(nextProviders);
      setSelectedProviderId(created.id);
      setShowProviderModal(false);
      resetProviderForm();
    } catch (error) {
      console.error("Error creating provider:", error);
      setProviderError(getProviderApiErrorMessage(error, "Error al crear proveedor."));
    } finally {
      setIsSavingProvider(false);
    }
  };

  const handleAddPaymentRow = () => {
    if (!canCreateStock) return;
    setPayments((prev) => [
      ...prev,
      { amount: "", method: "efectivo", reference: "", paidAt: today, voucherFile: null },
    ]);
  };

  const handlePaymentChange = (index, field, value) => {
    setPayments((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handlePaymentVoucherChange = (index, file) => {
    setPayments((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], voucherFile: file || null };
      return next;
    });
  };

  const handleRemovePaymentRow = (index) => {
    setPayments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateVariety = async () => {
    if (!canUpdateProduct) return;
    if (!newVarietyName) {
      notify("Por favor, ingresa el nombre de la variedad.");
      return;
    }

    try {
      let uploadedImageUrl = null;

      if (newVarietyFile) {
        const response = await uploadFile(newVarietyFile);
        uploadedImageUrl = response.url;
      }

      const newVariety = {
        varietyName: newVarietyName,
        varietyImage: uploadedImageUrl,
        description: newVarietyDescription, // Include description
        productId: modalProduct.id,
      };

      const createdVariety = await addVarietyToProduct(branchId, newVariety);

      // Update the product with the new variety
      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === modalProduct.id
            ? { ...product, Varieties: [...product.Varieties, createdVariety] }
            : product
        )
      );

      setModalProduct((prevProduct) => ({
        ...prevProduct,
        Varieties: [...prevProduct.Varieties, createdVariety],
      }));

      // Close Modal and Reset Form
      setShowNewVarietyModal(false);
      setNewVarietyName("");
      setNewVarietyImage(null);
      setNewVarietyFile(null);
      setNewVarietyDescription(""); // Reset description
    } catch (error) {
      console.error("Error creating variety:", error);
    }
  };

  const handleModalClose = () => {
    setModalVariety(null);
    setQuantity("");
    setStockPrice("");
    setStockPriceWithoutIva("");
    setMetricUnit("");
    setWeight("");
    setLengthCm("");
    setWidthCm("");
    setHeightCm("");
    setImportConversions([]);
    setConversionInputs({});
    setLoadingImportDefaults(false);
    setBultosPerBin("");
    setPayments([]);
    if (isImportList) setSelectedProviderId("");
    setProviderSearchTerm("");
    setIsProviderSelectOpen(false);
  };

  const handleMetricUnitChange = (value) => {
    setMetricUnit(value);
    if (!isBinUnit(value)) {
      setBultosPerBin("");
      return;
    }
    if (!bultosPerBin) {
      const lastValue = lastStockItems?.[modalVariety?.id]?.lastBinBultos;
      if (lastValue) {
        setBultosPerBin(String(lastValue));
      }
    }
  };

  useEffect(() => {
    const loadImportDefaults = async () => {
      if (!isImportList || !modalVariety?.id || !branchId) return;
      setLoadingImportDefaults(true);
      try {
        const [profilesResponse, conversionsResponse] = await Promise.all([
          getImportProfiles(branchId, [modalVariety.id]),
          getImportConversions(branchId, [modalVariety.id]),
        ]);
        const profile = Array.isArray(profilesResponse?.data)
          ? profilesResponse.data[0]
          : null;
        if (profile) {
          if (profile.metricUnit) setMetricUnit(profile.metricUnit);
          if (profile.price !== null && profile.price !== undefined) setStockPrice(String(profile.price));
          if (profile.priceWithoutIva !== null && profile.priceWithoutIva !== undefined) {
            setStockPriceWithoutIva(String(profile.priceWithoutIva));
          }
          if (profile.weightKg !== null && profile.weightKg !== undefined) setWeight(String(profile.weightKg));
          if (profile.lengthCm !== null && profile.lengthCm !== undefined) setLengthCm(String(profile.lengthCm));
          if (profile.widthCm !== null && profile.widthCm !== undefined) setWidthCm(String(profile.widthCm));
          if (profile.heightCm !== null && profile.heightCm !== undefined) setHeightCm(String(profile.heightCm));
          if (profile.providerId) setSelectedProviderId(profile.providerId);
        }
        setImportConversions(Array.isArray(conversionsResponse?.data) ? conversionsResponse.data : []);
        setConversionInputs({});
      } catch (error) {
        console.error("Error loading import defaults:", error);
      } finally {
        setLoadingImportDefaults(false);
      }
    };
    loadImportDefaults();
  }, [branchId, isImportList, modalVariety?.id]);

  const selectedVarietyPriceUnits = (() => {
    if (!modalVariety?.VarietyPrices) return [];
    const seen = new Set();
    const units = [];
    (modalVariety.VarietyPrices || []).forEach((price) => {
      const label = String(price?.metricUnit || "").trim();
      const key = toMetricUnitKey(label);
      if (!label || !key || seen.has(key)) return;
      seen.add(key);
      units.push({ key, label });
    });
    return units;
  })();

  const missingImportConversionUnits = (() => {
    if (!isImportList || !metricUnit) return [];
    const stockUnitKey = toMetricUnitKey(metricUnit);
    if (!stockUnitKey) return [];
    const existing = new Set(
      (importConversions || []).map((conversion) =>
        `${toMetricUnitKey(conversion.fromUnitKey || conversion.fromUnit)}::${toMetricUnitKey(conversion.toUnitKey || conversion.toUnit)}`
      )
    );
    return selectedVarietyPriceUnits.filter((unit) => {
      if (unit.key === stockUnitKey) return false;
      return !existing.has(`${stockUnitKey}::${unit.key}`);
    });
  })();

  const handleConversionInputChange = (unitKey, field, value) => {
    setConversionInputs((prev) => ({
      ...prev,
      [unitKey]: {
        fromAmount: prev[unitKey]?.fromAmount || "1",
        toAmount: prev[unitKey]?.toAmount || "",
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (isSavingStock) return;
    if (!canCreateStock) return;
    if (!quantity || (stockPrice === "" && stockPriceWithoutIva === "")) {
      notify("Por favor, completa todos los campos.");
      return;
    }
    if (!selectedProviderId) {
      notify("Selecciona un proveedor.");
      return;
    }
    if (isImportList) {
      const importWeight = Number(weight);
      const importLength = Number(lengthCm);
      const importWidth = Number(widthCm);
      const importHeight = Number(heightCm);
      if (!metricUnit) {
        notify("Selecciona la unidad de compra.");
        return;
      }
      if (!Number.isFinite(importWeight) || importWeight <= 0) {
        notify("Ingresa el peso en kg.");
        return;
      }
      if (
        !Number.isFinite(importLength) ||
        !Number.isFinite(importWidth) ||
        !Number.isFinite(importHeight) ||
        importLength <= 0 ||
        importWidth <= 0 ||
        importHeight <= 0
      ) {
        notify("Ingresa largo, ancho y alto en cm.");
        return;
      }
    }

    const priceWithIva = Number(stockPrice || 0);
    const priceWithoutIva = Number(stockPriceWithoutIva || 0);
    const totalAmounts = calculateStockAmounts({
      price: priceWithIva,
      priceWithoutIva,
      quantity,
    });
    const totalAmount = totalAmounts.providerTotalAmount;
    if (
      !Number.isFinite(priceWithIva) ||
      !Number.isFinite(priceWithoutIva) ||
      priceWithIva < 0 ||
      priceWithoutIva < 0 ||
      !Number.isFinite(totalAmount) ||
      totalAmount <= 0
    ) {
      notify("El total debe ser mayor a 0.");
      return;
    }

    const validatedPayments = [];
    for (let i = 0; i < payments.length; i++) {
      const payment = payments[i];
      const amount = Number(payment.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        notify(`Monto invalido en el pago ${i + 1}.`);
        return;
      }
      if (!payment.method) {
        notify(`Metodo de pago obligatorio en el pago ${i + 1}.`);
        return;
      }
      validatedPayments.push({
        amount,
        method: payment.method,
        reference: normalizeValue(payment.reference),
        paidAt: payment.paidAt || undefined,
        voucherFile: payment.voucherFile,
      });
    }

    const paymentTotal = validatedPayments.reduce((sum, item) => sum + item.amount, 0);
    if (paymentTotal > totalAmount) {
      notify("El total de pagos no puede superar el total de la compra.");
      return;
    }

    const isBin = isBinUnit(metricUnit);
    const bultosPerBinValue = Number(bultosPerBin);
    if (isBin) {
      if (!Number.isInteger(bultosPerBinValue) || bultosPerBinValue <= 0) {
        notify("Ingresa los bultos por bin.");
        return;
      }
    }

    setIsSavingStock(true);
    try {
      const normalizedConversions = [];
      if (isImportList) {
        for (const unit of missingImportConversionUnits) {
          const conversionInput = conversionInputs[unit.key] || {};
          const fromAmount = Number(conversionInput.fromAmount || 1);
          const toAmount = Number(conversionInput.toAmount);
          if (!Number.isFinite(fromAmount) || fromAmount <= 0 || !Number.isFinite(toAmount) || toAmount <= 0) {
            notify(`Ingresa la conversion desde ${metricUnit} a ${unit.label}.`);
            return;
          }
          normalizedConversions.push({
            varietyId: modalVariety.id,
            fromUnit: metricUnit,
            fromAmount,
            toUnit: unit.label,
            toAmount,
          });
        }
      }
      const normalizedPayments = [];
      for (let i = 0; i < validatedPayments.length; i++) {
        const payment = validatedPayments[i];
        let voucherUrl;
        if (payment.voucherFile) {
          try {
            const response = await uploadFile(payment.voucherFile);
            voucherUrl = response?.url;
          } catch (error) {
            console.error("Error subiendo comprobante:", error);
            notify(`Error al subir comprobante en el pago ${i + 1}.`);
            return;
          }
        }
        const paymentPayload = {
          amount: payment.amount,
          method: payment.method,
          reference: payment.reference,
          paidAt: payment.paidAt,
        };
        if (voucherUrl) {
          paymentPayload.voucherUrl = voucherUrl;
        }
        normalizedPayments.push(paymentPayload);
      }
      const stockPayload = {
        price: priceWithIva,
        priceWithoutIva,
        quantity,
        varietyId: modalVariety.id,
        varietyName: modalVariety.varietyName,
        varietyImage: modalVariety.varietyImage,
        metricUnit,
        weight,
        bultosPerBin: isBin ? bultosPerBinValue : undefined,
        providerId: selectedProviderId,
        payments: normalizedPayments,
        stockListId: id,
      };
      if (isImportList) {
        stockPayload.weightKg = Number(weight);
        stockPayload.weight = Number(weight);
        stockPayload.lengthCm = Number(lengthCm);
        stockPayload.widthCm = Number(widthCm);
        stockPayload.heightCm = Number(heightCm);
        stockPayload.conversions = normalizedConversions;
      }
      await createStockItem(branchId, stockPayload);
      setStockListVarietyCounts((prev) => ({
        ...prev,
        [modalVariety.id]: (prev[modalVariety.id] || 0) + 1,
      }));
      handleModalClose();
    } catch (error) {
      console.error("Error al crear el stock:", error);
    } finally {
      setIsSavingStock(false);
    }
  };

  const stockAmounts = calculateStockAmounts({
    price: stockPrice,
    priceWithoutIva: stockPriceWithoutIva,
    quantity,
  });
  const safeTotalAmount = Number.isFinite(stockAmounts.providerTotalAmount) ? stockAmounts.providerTotalAmount : 0;
  const safeStockCostAmount = Number.isFinite(stockAmounts.stockCostAmount) ? stockAmounts.stockCostAmount : 0;
  const safeIvaCostUpliftAmount = Number.isFinite(stockAmounts.ivaCostUpliftAmount)
    ? stockAmounts.ivaCostUpliftAmount
    : 0;
  const paidAmount = payments.reduce((sum, payment) => {
    const amount = Number(payment.amount || 0);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const balanceAmount = Math.max(safeTotalAmount - paidAmount, 0);
  const overpayAmount = Math.max(paidAmount - safeTotalAmount, 0);
  const normalizedProviderSearch = providerSearchTerm.trim().toLowerCase();
  const selectedProviderKey = selectedProviderId
    ? String(selectedProviderId)
    : "";
  const filteredProviders = providers.filter((provider) => {
    const providerId = String(provider.id);
    if (providerId === selectedProviderKey) return true;
    if (!normalizedProviderSearch) return true;
    const name = provider.name?.toLowerCase() || "";
    const rut = provider.rut?.toLowerCase() || "";
    const giro = provider.giro?.toLowerCase() || "";
    return (
      name.includes(normalizedProviderSearch) ||
      rut.includes(normalizedProviderSearch) ||
      giro.includes(normalizedProviderSearch)
    );
  });
  const selectedProvider =
    selectedProviderKey &&
    providers.find((provider) => String(provider.id) === selectedProviderKey);
  const selectedProviderLabel = selectedProvider?.name || "Selecciona un proveedor";
  const providerSelectDisabled = !canCreateStock || !canReadProvider;

  return (
    <>
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Title */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Registro de Compras</h1>
            <button
              onClick={() => navigate(`/branches/${branchId}/stock`)}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Volver
            </button>
          </div>

          {!canReadProducts ? (
            <p className="text-sm text-gray-600">
              No tienes permisos para ver productos.
            </p>
          ) : (
            <StockProductPickerPanel
              products={products}
              categories={displayCategories}
              categoriesLoading={categoriesLoading}
              categoriesError={categoriesError}
              resolveProductCategoryId={resolveProductCategoryId}
              resolveCategoryLabel={resolveCategoryLabel}
              onSelectVariety={(variety, product) => {
                if (isImportList) {
                  setSelectedProviderId("");
                }
                setModalProduct(product);
                setModalVariety({
                  ...variety,
                  productName: product?.productName || variety?.productName,
                });
              }}
              getVarietyBadge={(variety) => {
                const addedCount = Number(stockListVarietyCounts[variety.id] || 0);
                const isAlreadyInList = addedCount > 0;
                return {
                  label: isAlreadyInList
                    ? `Agregado${addedCount > 1 ? ` x${addedCount}` : ""}`
                    : "Faltante",
                  cardClassName: isAlreadyInList
                    ? "bg-emerald-50/70 border-emerald-300 ring-1 ring-emerald-200"
                    : "bg-amber-50/70 border-amber-300",
                  badgeClassName: isAlreadyInList
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700",
                };
              }}
              renderVarietyReference={(variety, context) => {
                const wholesalePrices = (context?.sellablePrices || []).filter(
                  (price) =>
                    resolvePriceClientType(price) === "mayorista"
                    && Number.isFinite(Number(price.price))
                );
                const wholesaleReference =
                  wholesalePrices.length > 0
                    ? [...wholesalePrices].sort(
                        (entryA, entryB) =>
                          Number(entryA.price) - Number(entryB.price)
                      )[0]
                    : null;
                const lastPurchase = lastStockItems[variety.id] || null;
                return (
                  <>
                    {context?.isVarietySoldOut ? (
                      <p className="text-red-600 text-xs font-semibold mt-1">
                        AGOTADO
                      </p>
                    ) : wholesaleReference ? (
                      <p className="text-slate-600 text-xs mt-1">
                        Mayorista ref: $
                        {Number(wholesaleReference.price || 0).toLocaleString("de-DE")}{" "}
                        {wholesaleReference.metricUnit || ""}
                      </p>
                    ) : (
                      <p className="text-slate-500 text-xs mt-1">
                        Sin precio mayorista
                      </p>
                    )}
                    <p className="text-slate-500 text-xs mt-1">
                      Última compra:{" "}
                      {lastPurchase
                        ? `$${Number(lastPurchase.price || 0).toLocaleString("de-DE")}`
                        : "Sin registro"}
                    </p>
                  </>
                );
              }}
              renderProductAction={({ product }) =>
                canUpdateProduct ? (
                  <button
                    className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800"
                    onClick={() => {
                      setModalProduct(product);
                      setShowNewVarietyModal(true);
                    }}
                    type="button"
                  >
                    + Agregar Tipo
                  </button>
                ) : null
              }
              emptyStateLabel="No hay productos disponibles."
            />
          )}
        </div>
      </div>

      {/* Modal */}
      {modalVariety && (
        <div
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm flex justify-center items-start z-50 px-4 py-4 md:py-8 overflow-y-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-slate-50 rounded-lg shadow-2xl w-full max-w-4xl border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <div className="flex items-start justify-between gap-4 bg-white border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Registrar compra
                </p>
                <h2 className="text-2xl font-semibold text-slate-950 leading-tight">
                  {modalVariety.productName}
                </h2>
                {modalVariety.varietyName && (
                  <p className="mt-1 text-sm text-slate-500">{modalVariety.varietyName}</p>
                )}
              </div>
              <button
                onClick={handleModalClose}
                className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Last Stock Info */}
            {lastStockItems[modalVariety.id] && (
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Última compra</p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-slate-700">
                  <span>
                    {new Date(lastStockItems[modalVariety.id].createdAt).toLocaleDateString()}
                  </span>
                  <span>
                    Monto comprado con IVA: ${Number(lastStockItems[modalVariety.id].price || 0).toLocaleString("de-DE")}
                  </span>
                  <span>
                    Monto comprado sin IVA: ${Number(lastStockItems[modalVariety.id].priceWithoutIva || 0).toLocaleString("de-DE")}
                  </span>
                  <span>
                    Cantidad: {lastStockItems[modalVariety.id].quantity}
                  </span>
                </div>
              </div>
            )}
            {/* Quantity and Price Inputs */}
            <div className={modalSectionClass}>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Compra</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={modalLabelClass}>
                    Cantidad
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className={modalInputClass}
                    disabled={!canCreateStock}
                  />
                </div>
                <div>
                  <label className={modalLabelClass}>Monto comprado con IVA</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={stockPrice}
                    onChange={(e) => setStockPrice(e.target.value)}
                    className={modalInputClass}
                    disabled={!canCreateStock}
                  />
                </div>
                <div>
                  <label className={modalLabelClass}>Monto comprado sin IVA</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={stockPriceWithoutIva}
                    onChange={(e) => setStockPriceWithoutIva(e.target.value)}
                    className={modalInputClass}
                    disabled={!canCreateStock}
                  />
                </div>
              </div>
            </div>
            {/* Metric Unit Selection */}
            <div className={modalSectionClass}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-sm font-semibold text-slate-900">Unidad y peso</h3>
                {isImportList && (
                  <span className="rounded-full bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-700">
                    Importaciones
                  </span>
                )}
              </div>
              {loadingImportDefaults && (
                <p className="text-xs text-slate-500 mb-3">Cargando datos guardados de importación...</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={modalLabelClass}>
                    {isImportList ? "Unidad de compra" : "Unidad Métrica"}
                  </label>
                  <select
                    value={metricUnit}
                    onChange={(e) => handleMetricUnitChange(e.target.value)}
                    className={modalInputClass}
                    required
                    disabled={!canCreateStock}
                  >
                    <option value="">Selecciona una unidad</option>
                    <option value="gamela">Gamela</option>
                    <option value="balde">Balde</option>
                    <option value="granel">Granel</option>
                    <option value="bin">Bin</option>
                    <option value="malla">Malla</option>
                    <option value="cajonMadera">Cajón de madera</option>
                    <option value="caja">Caja</option>
                    <option value="saco">Saco</option>
                    <option value="cajonNegro">Cajón Negro</option>
                    <option value="cajonBlanco">Cajón Blanco</option>
                  </select>
                </div>

                {isBinUnit(metricUnit) && (
                  <div>
                    <label className={modalLabelClass}>
                      Bultos por bin
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step="1"
                      value={bultosPerBin}
                      onChange={(e) => setBultosPerBin(e.target.value)}
                      className={modalInputClass}
                      disabled={!canCreateStock}
                    />
                  </div>
                )}

                <div>
                  <label className={modalLabelClass}>
                    {isImportList ? "Peso por unidad (kg)" : "Peso (kg)"}
                  </label>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className={modalInputClass}
                    disabled={!canCreateStock}
                  />
                </div>
                {isImportList && (
                  <>
                    <div>
                      <label className={modalLabelClass}>Largo (cm)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={lengthCm}
                        onChange={(e) => setLengthCm(e.target.value)}
                        className={modalInputClass}
                        disabled={!canCreateStock}
                      />
                    </div>
                    <div>
                      <label className={modalLabelClass}>Ancho (cm)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={widthCm}
                        onChange={(e) => setWidthCm(e.target.value)}
                        className={modalInputClass}
                        disabled={!canCreateStock}
                      />
                    </div>
                    <div>
                      <label className={modalLabelClass}>Alto (cm)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={heightCm}
                        onChange={(e) => setHeightCm(e.target.value)}
                        className={modalInputClass}
                        disabled={!canCreateStock}
                      />
                    </div>
                  </>
                )}
              </div>
              {isImportList && metricUnit && missingImportConversionUnits.length > 0 && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">
                    Conversiones requeridas
                  </p>
                  <div className="space-y-3">
                    {missingImportConversionUnits.map((unit) => {
                      const input = conversionInputs[unit.key] || { fromAmount: "1", toAmount: "" };
                      return (
                        <div key={unit.key} className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 items-end">
                          <div>
                            <label className={modalLabelClass}>Cantidad compra</label>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                value={input.fromAmount}
                                onChange={(e) => handleConversionInputChange(unit.key, "fromAmount", e.target.value)}
                                className={modalInputClass}
                                disabled={!canCreateStock}
                              />
                              <span className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                                {metricUnit}
                              </span>
                            </div>
                          </div>
                          <div className="pb-2 text-center text-sm font-semibold text-amber-700">=</div>
                          <div>
                            <label className={modalLabelClass}>Cantidad precio</label>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                value={input.toAmount}
                                onChange={(e) => handleConversionInputChange(unit.key, "toAmount", e.target.value)}
                                className={modalInputClass}
                                disabled={!canCreateStock}
                              />
                              <span className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                                {unit.label}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Provider */}
            <div className={modalSectionClass}>
              <label className={modalLabelClass}>
                Proveedor
              </label>
              <div className="flex gap-2">
                <div className="relative w-full" ref={providerSelectRef}>
                  <button
                    type="button"
                    onClick={() => setIsProviderSelectOpen((prev) => !prev)}
                    className={`${modalInputClass} flex items-center justify-between text-left`}
                    aria-haspopup="listbox"
                    aria-expanded={isProviderSelectOpen}
                    disabled={providerSelectDisabled}
                  >
                    <span className={selectedProvider ? "text-gray-900" : "text-gray-500"}>
                      {selectedProviderLabel}
                    </span>
                    <span className="text-gray-500">▾</span>
                  </button>
                  {isProviderSelectOpen && (
                    <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden">
                      <div className="p-2 border-b border-gray-100">
                        <input
                          type="text"
                          placeholder="Buscar proveedor..."
                          value={providerSearchTerm}
                          onChange={(e) => setProviderSearchTerm(e.target.value)}
                          className={modalInputClass}
                          disabled={providerSelectDisabled}
                        />
                      </div>
                      <ul className="max-h-60 overflow-y-auto" role="listbox">
                        {filteredProviders.length === 0 ? (
                          <li className="px-3 py-2 text-sm text-gray-500">
                            {providerSearchTerm ? "Sin resultados" : "No hay proveedores"}
                          </li>
                        ) : (
                          filteredProviders.map((provider) => {
                            const isSelected =
                              String(provider.id) === selectedProviderKey;
                            return (
                              <li key={provider.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedProviderId(provider.id);
                                    setProviderSearchTerm("");
                                    setIsProviderSelectOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                                    isSelected ? "bg-gray-100 font-medium" : ""
                                  }`}
                                  role="option"
                                  aria-selected={isSelected}
                                >
                                  <span>{provider.name}</span>
                                  {(provider.rut || provider.giro) && (
                                    <span className="block text-xs text-gray-500">
                                      {[provider.rut, provider.giro]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </span>
                                  )}
                                </button>
                              </li>
                            );
                          })
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                {canCreateProvider && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsProviderSelectOpen(false);
                      setShowProviderModal(true);
                    }}
                    className="shrink-0 rounded-lg bg-slate-900 text-white px-3 py-2 text-sm font-semibold hover:bg-slate-800"
                  >
                    + Nuevo
                  </button>
                )}
              </div>
              {!canReadProvider && (
                <p className="text-xs text-gray-500 mt-2">
                  No tienes permisos para ver proveedores.
                </p>
              )}
            </div>

            {/* Payments */}
            <div className={modalSectionClass}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Pagos</h3>
                  <p className="text-xs text-slate-500">Los pagos se imputan solo al monto adeudado al proveedor.</p>
                </div>
                {canCreateStock && (
                  <button
                    type="button"
                    onClick={handleAddPaymentRow}
                    className="shrink-0 text-sm bg-slate-900 text-white px-3 py-2 rounded-lg hover:bg-slate-800"
                  >
                    + Agregar pago
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Adeudado proveedor</p>
                  <p className="text-sm font-semibold text-slate-900">${safeTotalAmount.toLocaleString("de-DE")}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Pagado</p>
                  <p className="text-sm font-semibold text-slate-900">${paidAmount.toLocaleString("de-DE")}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Pendiente</p>
                  <p className="text-sm font-semibold text-slate-900">${balanceAmount.toLocaleString("de-DE")}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Costo stock</p>
                  <p className="text-sm font-semibold text-slate-900">${safeStockCostAmount.toLocaleString("de-DE")}</p>
                  <p className="text-[11px] text-slate-500">IVA no adeudado: ${safeIvaCostUpliftAmount.toLocaleString("de-DE")}</p>
                </div>
              </div>
              {overpayAmount > 0 && (
                <p className="text-xs text-red-600 mb-2">
                  Estas pagando de mas por ${overpayAmount.toLocaleString("de-DE")}.
                </p>
              )}
              {payments.length === 0 ? (
                <p className="text-sm text-gray-600">Sin pagos registrados.</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment, index) => {
                    const runningTotal = payments
                      .slice(0, index + 1)
                      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
                    const remainingAfter = Math.max(safeTotalAmount - runningTotal, 0);
                    const overpayAfter = Math.max(runningTotal - safeTotalAmount, 0);

                    return (
                    <div
                      key={`payment-${index}`}
                      className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input
                          type="number"
                          placeholder="Monto"
                          value={payment.amount}
                          onChange={(e) =>
                            handlePaymentChange(index, "amount", e.target.value)
                          }
                          className={modalInputClass}
                          disabled={!canCreateStock}
                        />
                        <select
                          value={payment.method}
                          onChange={(e) =>
                            handlePaymentChange(index, "method", e.target.value)
                          }
                          className={modalInputClass}
                          disabled={!canCreateStock}
                        >
                          <option value="">Metodo</option>
                          {paymentMethods.map((method) => (
                            <option key={method.id} value={method.id}>
                              {method.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Referencia"
                          value={payment.reference}
                          onChange={(e) =>
                            handlePaymentChange(
                              index,
                              "reference",
                              e.target.value
                            )
                          }
                          className={modalInputClass}
                          disabled={!canCreateStock}
                        />
                        <input
                          type="date"
                          value={payment.paidAt}
                          onChange={(e) =>
                            handlePaymentChange(index, "paidAt", e.target.value)
                          }
                          className={modalInputClass}
                          disabled={!canCreateStock}
                        />
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Comprobante (opcional)
                          </label>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) =>
                              handlePaymentVoucherChange(
                                index,
                                e.target.files?.[0] || null
                              )
                            }
                            className={modalInputClass}
                            disabled={!canCreateStock}
                          />
                          {payment.voucherFile?.name && (
                            <p className="text-xs text-gray-500 mt-1">
                              {payment.voucherFile.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mt-2">
                        Restante despues de este pago: ${remainingAfter.toLocaleString("de-DE")}
                      </div>
                      {overpayAfter > 0 && (
                        <div className="text-xs text-red-600 mt-1">
                          Estas pagando de mas por ${overpayAfter.toLocaleString("de-DE")}.
                        </div>
                      )}
                      <div className="flex justify-end mt-2">
                        {canCreateStock && (
                          <button
                            type="button"
                            onClick={() => handleRemovePaymentRow(index)}
                            className="rounded-lg bg-red-600 text-white px-3 py-1.5 text-sm hover:bg-red-700"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>

            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
              <button
                onClick={handleModalClose}
                className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-300 hover:bg-slate-100"
                type="button"
              >
                Cancelar
              </button>
              {canCreateStock && (
                <button
                  onClick={handleSave}
                  disabled={isSavingStock}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-60"
                  type="button"
                >
                  {isSavingStock ? <InlineLoading label="Guardando" /> : "Guardar"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {showNewVarietyModal && canUpdateProduct && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nuevo tipo
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Agregar tipo
                </h2>
              </div>
              <button
                onClick={() => setShowNewVarietyModal(false)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Image Upload */}
              <div className="flex justify-center">
                <div className="relative w-[150px] h-[150px]">
                  <img
                    src={newVarietyImage || "https://placehold.co/150"}
                    alt="Variety"
                    className="absolute inset-0 w-full h-full object-cover rounded-xl border border-slate-200"
                  />
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                className="border border-slate-300 rounded-lg p-2 w-full"
                onChange={(e) => {
                  setNewVarietyImage(URL.createObjectURL(e.target.files[0]));
                  setNewVarietyFile(e.target.files[0]);
                }}
              />

              {/* Variety Name Input */}
              <label className="block text-sm font-medium text-slate-700">
                Nombre del tipo de producto
              </label>
              <input
                type="text"
                placeholder="Ej: Ají jalapeño"
                className="border border-slate-300 rounded-lg p-2 w-full"
                value={newVarietyName}
                onChange={(e) => setNewVarietyName(e.target.value)}
              />

              {/* Variety Description Input */}
              <label className="block text-sm font-medium text-slate-700">
                Descripción
              </label>
              <textarea
                placeholder="Ej: Ají Jalapeño, comprado en balde."
                className="border border-slate-300 rounded-lg p-2 w-full"
                value={newVarietyDescription}
                onChange={(e) => setNewVarietyDescription(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                onClick={() => setShowNewVarietyModal(false)}
                className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                type="button"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleCreateVariety()}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                type="button"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {showProviderModal && canCreateProvider && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nuevo proveedor
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Registrar proveedor
                </h2>
              </div>
              <button
                onClick={handleProviderModalClose}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSaveProvider} className="space-y-4 px-6 py-5">
              {providerError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                  {providerError}
                </div>
              )}
              <ProviderFormFields
                form={providerForm}
                onChange={handleProviderFormChange}
                onRutChange={handleProviderRutChange}
                accounts={providerAccounts}
                onAccountAdd={handleAddProviderAccount}
                onAccountRemove={handleRemoveProviderAccount}
                onAccountChange={handleProviderAccountChange}
                formatRut={formatRut}
                disabled={!canCreateProvider}
                showProviderTitle={false}
                showAccountsTitle
                accountsTitle="Cuentas bancarias"
                accountsTitleClassName="font-semibold mb-2"
                containerClassName="space-y-4"
              />
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 -mx-6 px-6 py-4">
                <button
                  type="button"
                  onClick={handleProviderModalClose}
                  className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSavingProvider}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {isSavingProvider ? <InlineLoading label="Guardando" /> : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default StockCreatePage;
